'use client';

import React from 'react';
import {usePathname} from 'next/navigation';
import {Box, Typography, styled, keyframes} from '@mui/material';
import Link from 'next/link';

type ActiveLinkItem = {
  label?: string | null;
  linkTo?: string | null;
  matchingUrlPath?: string | null;
};

type HeroMediaOverlayProps = {
  heading?: string | null;
  subText?: string | null;
  links: Array<ActiveLinkItem | null>;
};

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, calc(-50% + 30px));
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%);
  }
`;

const StyledOverlay = styled(Box)(({theme}) => ({
  position: 'absolute',
  top: '60%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(6, 2, 14),
  zIndex: 2,
  pointerEvents: 'none',
  animation: `${fadeInUp} 0.8s ease-out forwards`,
  '& > *': {
    pointerEvents: 'auto',
  },
}));

const StyledLinksContainer = styled(Box)(({theme}) => ({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginTop: theme.spacing(2),
}));

const StyledPipe = styled('span')(({theme}) => ({
  userSelect: 'none',
  fontSize: theme.typography.h3.fontSize,
  color: theme.palette.text.primary,
}));

const StyledLink = styled(Link)(({theme}) => ({
  color: theme.palette.text.linkSecondary,
  textDecoration: 'none',
  fontSize: theme.typography.h3.fontSize,
  transition: 'opacity 0.2s',
  '&:hover': {
    opacity: 0.8,
  },
}));

const StyledCurrentLabel = styled('span')(({theme}) => ({
  color: theme.palette.text.primary,
  fontSize: theme.typography.h3.fontSize,
}));

export default function HeroMediaOverlay({heading, subText, links}: HeroMediaOverlayProps) {
  const pathname = usePathname();
  const validLinks = links.filter(
    (link): link is ActiveLinkItem & {label: string; linkTo: string} =>
      !!link?.label && !!link?.linkTo
  );

  if (!heading && !subText && validLinks.length === 0) return null;

  return (
    <StyledOverlay>
      {heading && (
        <Typography variant='h1' color='text.primary' sx={{textAlign: 'center'}}>
          {heading}
        </Typography>
      )}
      {subText && (
        <Typography variant='h2' color='text.primary' sx={{textAlign: 'center', mt: 1, opacity: 0.9}}>
          {subText}
        </Typography>
      )}
      {validLinks.length > 0 && (
        <StyledLinksContainer>
          {validLinks.map((link, index) => {
            const isCurrentPage = link.matchingUrlPath
              ? pathname.includes(link.matchingUrlPath)
              : pathname === link.linkTo;

            return (
              <React.Fragment key={link.label}>
                {index > 0 && <StyledPipe>|</StyledPipe>}
                {isCurrentPage ? (
                  <StyledCurrentLabel>{link.label}</StyledCurrentLabel>
                ) : (
                  <StyledLink href={link.linkTo}>{link.label}</StyledLink>
                )}
              </React.Fragment>
            );
          })}
        </StyledLinksContainer>
      )}
    </StyledOverlay>
  );
}
